import { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  CopyRegular,
  CheckmarkRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { useAuth } from '@/auth';
import { useAppStore } from '@/stores/appStore';
import { useTenants } from '@/hooks/useAzureResources';
import { useSettingCardStyles } from './sectionStyles';

export interface ProfileSectionProps {
  /** MS Graph photo URL passed in from the modal shell so we don't
   *  subscribe to `useUserPhoto` twice in the same tree. */
  photo?: string | undefined;
}

const useProfileStyles = makeStyles({
  // Hero row mirrors the About section: large avatar + identity block,
  // separated from the details list by a single hairline. Keeps the
  // borderless feel of the rest of the modal.
  hero: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    paddingBlockEnd: tokens.spacingVerticalL,
    borderBlockEndWidth: '1px',
    borderBlockEndStyle: 'solid',
    borderBlockEndColor: tokens.colorNeutralStroke3,
  },
  heroText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  heroName: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  heroEmail: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Stack tenant name above the GUID in the active-tenant row.
  tenantStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  tenantId: {
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    padding: tokens.spacingHorizontalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

function getInitials(name: string | undefined, fallback: string | undefined): string {
  const source = name ?? fallback ?? '';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Account profile: shows the signed-in identity (display name, email),
 * the active tenant (with copyable tenant ID), and a sign-out action.
 *
 * Read-only: anything mutable about the account (tenant *switch*, region
 * default, etc.) lives in General. This section's job is to make the
 * current identity legible and provide the one destructive action that
 * doesn't belong anywhere else (sign out).
 */
export const ProfileSection = memo<ProfileSectionProps>(({ photo }) => {
  const styles = useSettingCardStyles();
  const profileStyles = useProfileStyles();
  const { account, logout } = useAuth();
  const selectedTenantId = useAppStore((s) => s.selectedTenantId);
  const { data: tenants, isPending: tenantsPending, isError: tenantsError } = useTenants();

  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Always clear pending copy-confirmation timers on unmount so the
  // setState never fires after the section is gone (avoids a React
  // warning when users navigate away mid-feedback).
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopyTenantId = useCallback(async () => {
    if (!selectedTenantId) return;
    try {
      await navigator.clipboard.writeText(selectedTenantId);
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be unavailable (non-secure context, denied
      // permission). The tenant ID is still rendered as plain text so
      // the user can select and copy it manually.
    }
  }, [selectedTenantId]);

  if (!account) {
    return (
      <div className={profileStyles.emptyState}>
        <Text>You are not signed in.</Text>
      </div>
    );
  }

  const displayName = account.name ?? account.username ?? 'Signed-in user';
  const email = account.username ?? '';
  const activeTenant = tenants?.find((t) => t.tenantId === selectedTenantId);
  // Order matters: "no tenant selected yet" is a different UX state from
  // "tenants failed to load" which is different from "still loading". We
  // surface each explicitly so the row never claims to be loading forever
  // when the query has errored, and never claims a missing tenant when the
  // app just hasn't auto-selected one yet.
  let activeTenantName: string;
  if (!selectedTenantId) {
    activeTenantName = 'Not selected';
  } else if (tenantsError) {
    activeTenantName = 'Unavailable';
  } else if (tenantsPending) {
    activeTenantName = 'Loading…';
  } else {
    activeTenantName = activeTenant?.displayName ?? 'Unknown tenant';
  }

  return (
    <>
      <div className={profileStyles.hero}>
        <Avatar
          size={56}
          name={displayName}
          {...(photo ? { image: { src: photo } } : {})}
          initials={getInitials(account.name, account.username)}
          color="brand"
        />
        <div className={profileStyles.heroText}>
          <p className={profileStyles.heroName}>{displayName}</p>
          {email && <p className={profileStyles.heroEmail}>{email}</p>}
        </div>
      </div>

      <div className={styles.list}>
        <div className={styles.listRow}>
          <div>
            <p className={styles.cardLabel}>Name</p>
          </div>
          <div className={styles.cardControl}>
            <Text>{displayName}</Text>
          </div>
        </div>

        <div className={styles.listRow}>
          <div>
            <p className={styles.cardLabel}>Email</p>
            <p className={styles.cardHelp}>The account used to sign in.</p>
          </div>
          <div className={styles.cardControl}>
            <Text>{email || 'Not available'}</Text>
          </div>
        </div>

        <div className={styles.listRow}>
          <div className={profileStyles.tenantStack}>
            <p className={styles.cardLabel}>Active tenant</p>
            <p className={styles.cardHelp}>{activeTenantName}</p>
            {selectedTenantId && (
              <Text
                className={mergeClasses(styles.mono, profileStyles.tenantId)}
                title={selectedTenantId}
              >
                {selectedTenantId}
              </Text>
            )}
          </div>
          <div className={styles.cardControl}>
            <Button
              appearance="secondary"
              size="small"
              icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
              onClick={handleCopyTenantId}
              disabled={!selectedTenantId}
              aria-label={copied ? 'Tenant ID copied' : 'Copy tenant ID'}
            >
              {copied ? 'Copied' : 'Copy ID'}
            </Button>
          </div>
        </div>

        <div className={styles.listRow}>
          <div>
            <p className={styles.cardLabel}>Sign out</p>
          </div>
          <div className={styles.cardControl}>
            <Button
              appearance="secondary"
              icon={<SignOutRegular />}
              onClick={() => {
                void logout();
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </>
  );
});

ProfileSection.displayName = 'ProfileSection';
